"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Zap } from "lucide-react";
import { fadeUp, scaleIn, Section } from "./utils";

type BillingCycle = "weekly" | "monthly" | "annual";

const plans: Record<
  BillingCycle,
  { price: string; period: string; badge?: string; savings?: string }
> = {
  weekly: { price: "$4.99", period: "/week" },
  monthly: { price: "$14.99", period: "/month" },
  annual: {
    price: "$99.99",
    period: "/year",
    badge: "Best Value",
    savings: "Save 36%",
  },
};

const features = [
  "Full AI agent crew (Sales, Ops, Client Success)",
  "Credits included with every plan",
  "Real-time pipeline management",
  "24/7 autonomous lead management",
  "Multi-agent collaboration & handoff",
  "Agent memory & context persistence",
  "Custom agent training on your data",
  "Priority support",
];

export function Pricing() {
  const [cycle, setCycle] = useState<BillingCycle>("annual");
  const plan = plans[cycle];

  return (
    <Section id="pricing" className="px-6 py-28 max-w-4xl mx-auto">
      <motion.div variants={fadeUp} className="text-center mb-14">
        <p className="text-sm font-medium text-coral-400 mb-3 uppercase tracking-wider">
          Simple pricing
        </p>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-content">
          One plan. Full power.
        </h2>
        <p className="mt-3 text-content-muted max-w-md mx-auto">
          No tiers to compare. No features locked behind upgrades.
          Every plan gets the complete Tethra experience.
        </p>
      </motion.div>

      {/* Billing toggle */}
      <motion.div
        variants={fadeUp}
        custom={1}
        className="flex items-center justify-center gap-1 mb-12"
      >
        <div className="inline-flex items-center bg-surface-secondary border border-surface-border rounded-full p-1">
          {(["weekly", "monthly", "annual"] as BillingCycle[]).map((c) => (
            <button
              key={c}
              onClick={() => setCycle(c)}
              className={`relative px-5 py-2 text-sm font-medium rounded-full transition-all duration-200 cursor-pointer ${
                cycle === c
                  ? "bg-surface-tertiary text-content shadow-sm"
                  : "text-content-muted hover:text-content-secondary"
              }`}
            >
              {c === "weekly" && "Weekly"}
              {c === "monthly" && "Monthly"}
              {c === "annual" && (
                <span className="flex items-center gap-2">
                  Annual
                  <span className="text-[10px] font-semibold text-coral-400 bg-coral-500/10 border border-coral-500/20 rounded-full px-2 py-0.5">
                    -36%
                  </span>
                </span>
              )}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Pricing card */}
      <motion.div
        variants={scaleIn}
        custom={2}
        className="relative bg-surface-secondary/50 border border-surface-border rounded-2xl overflow-hidden"
      >
        {plan.badge && (
          <div className="absolute top-0 right-0">
            <div className="bg-coral-500 text-white text-[10px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-bl-xl">
              {plan.badge}
            </div>
          </div>
        )}

        <div className="p-10 sm:p-12">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
            <div>
              <div className="flex items-baseline gap-1">
                <motion.span
                  key={cycle}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="text-5xl sm:text-6xl font-bold tracking-tight text-content"
                >
                  {plan.price}
                </motion.span>
                <span className="text-lg text-content-muted">{plan.period}</span>
              </div>
              {plan.savings && (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-2 text-sm text-ok font-medium"
                >
                  {plan.savings} compared to monthly
                </motion.p>
              )}
              {cycle === "weekly" && (
                <p className="mt-2 text-sm text-content-muted">
                  Try it for a week — no commitment
                </p>
              )}
              {cycle === "monthly" && (
                <p className="mt-2 text-sm text-content-muted">
                  Flexible month-to-month billing
                </p>
              )}
            </div>

            <button className="group bg-coral-500 hover:bg-coral-400 text-white font-semibold rounded-full px-8 py-4 text-sm transition-all duration-300 active:scale-95 shadow-lg shadow-coral-500/25 hover:shadow-xl hover:shadow-coral-500/40 cursor-pointer whitespace-nowrap">
              Start Free Trial
              <ArrowRight
                size={16}
                className="inline ml-2 group-hover:translate-x-0.5 transition-transform"
              />
            </button>
          </div>

          <div className="border-t border-surface-border pt-8">
            <p className="text-[10px] uppercase tracking-widest font-mono text-content-muted mb-6">
              Everything included
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature) => (
                <div key={feature} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-coral-500/10 border border-coral-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={12} className="text-coral-400" />
                  </div>
                  <span className="text-sm text-content-secondary leading-snug">
                    {feature}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="px-10 sm:px-12 py-5 bg-surface-tertiary/30 border-t border-surface-border flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-content-muted">
            <Zap size={14} className="text-coral-400" />
            14-day free trial · No credit card required · Cancel anytime
          </div>
          <a
            href="#"
            className="text-xs text-content-muted hover:text-content-secondary transition-colors underline underline-offset-4"
          >
            Need a custom plan? Talk to us
          </a>
        </div>
      </motion.div>
    </Section>
  );
}
